import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MobileOKResult } from '../libs/MobileOKService';
import axiosClient from '../libs/axiosClient';

/**
 * Mobile-OK 본인확인 콜백 페이지
 *
 * 라우터에 등록 필요:
 * <Route path="/auth/mobileok/callback" element={<MobileOKCallback />} />
 *
 * 두 가지 방식 지원:
 * 1. callback 방식 (팝업): window.opener.postMessage로 결과 전달
 * 2. redirect 방식 (인앱 브라우저): 저장된 복귀 경로로 redirect
 */
function MobileOKCallback() {
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState<MobileOKResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('본인인증 처리 중...');

  useEffect(() => {
    const processCallback = async () => {
      try {
        console.log('[MobileOKCallback] Processing callback...');
        console.log('[MobileOKCallback] URL params:', Object.fromEntries(searchParams.entries()));
        console.log('[MobileOKCallback] Has opener:', !!window.opener);

        // 1. URL에서 clientTxId 먼저 확인 (redirect 방식)
        let txId = searchParams.get('clientTxId');

        // 2. sessionStorage 폴백 (callback 방식)
        if (!txId) {
          txId = sessionStorage.getItem('mobileok_txId');
          console.log('[MobileOKCallback] txId from sessionStorage:', txId);
        } else {
          console.log('[MobileOKCallback] txId from URL:', txId);
        }

        // 3. mokToken 확인 (백엔드에서 결과 조회 필요)
        const mokToken = searchParams.get('mokToken');
        const resultCode = searchParams.get('resultCode');
        const resultMsg = searchParams.get('resultMsg');

        // 에러 체크 (Mobile-OK 성공 코드: 2000)
        if (resultCode && resultCode !== '2000') {
          const errorMsg = resultMsg || '본인확인에 실패했습니다.';
          handleError(new Error(errorMsg));
          return;
        }

        // mokToken이 있으면 백엔드에서 결과 조회
        if (mokToken && txId) {
          setStatusMessage('인증 결과를 조회하고 있습니다...');

          try {
            const response = await axiosClient.post('/func/mobileok/result', {
              mokToken,
              txId,
            });

            console.log('[MobileOKCallback] Result API response:', response.data);

            // sessionStorage 정리
            sessionStorage.removeItem('mobileok_txId');

            // Mobile-OK 성공 코드: 0000
            if (response.data.resultCode === '0000') {
              const mobileOKResult: MobileOKResult = {
                resultCode: response.data.resultCode,
                resultMsg: response.data.resultMsg || '인증 성공',
                txId: response.data.txId,
                ci: response.data.ci,
                di: response.data.di,
                name: response.data.name,
                birthDate: response.data.birthDate,
                gender: response.data.gender,
                nationalInfo: response.data.nationalInfo,
                mobileNo: response.data.mobileNo,
                mobileCo: response.data.mobileCo,
              };
              handleSuccess(mobileOKResult);
            } else {
              handleError(new Error(response.data.resultMsg || '인증 결과 조회에 실패했습니다.'));
            }
          } catch (apiError) {
            console.error('[MobileOKCallback] Result API error:', apiError);
            handleError(apiError instanceof Error ? apiError : new Error('인증 결과 조회 중 오류가 발생했습니다.'));
          }
          return;
        }

        // txId도 mokToken도 없는 경우
        if (!txId && !mokToken) {
          handleError(new Error('인증 정보가 유효하지 않습니다. 다시 시도해주세요.'));
          return;
        }

        // mokToken 없이 txId만 있는 경우 (에러 상황)
        handleError(new Error('인증 토큰이 없습니다. 다시 시도해주세요.'));

      } catch (err) {
        console.error('[MobileOKCallback] Unexpected error:', err);
        handleError(err instanceof Error ? err : new Error('인증 처리 중 오류가 발생했습니다.'));
      }
    };

    const handleSuccess = (mobileOKResult: MobileOKResult) => {
      console.log('[MobileOKCallback] Auth success:', mobileOKResult);
      setResult(mobileOKResult);
      setIsLoading(false);

      // 부모 창이 있으면 postMessage (callback 방식)
      if (window.opener) {
        console.log('[MobileOKCallback] Sending postMessage to opener');
        window.opener.postMessage({
          type: 'MOBILEOK_CALLBACK',
          success: 'true',
          message: '인증 성공',
          data: {
            name: mobileOKResult.name,
            ci: mobileOKResult.ci,
            di: mobileOKResult.di,
            birthDate: mobileOKResult.birthDate,
            gender: mobileOKResult.gender,
            mobileNo: mobileOKResult.mobileNo,
            mobileCo: mobileOKResult.mobileCo,
          },
        }, window.location.origin);
        setTimeout(() => window.close(), 1500);
      } else {
        // 부모 창이 없으면 저장된 복귀 경로로 redirect (redirect 방식)
        const returnPath = sessionStorage.getItem('auth_return_path');
        console.log('[MobileOKCallback] No opener, return path:', returnPath);

        // 세션 정리
        sessionStorage.removeItem('auth_return_path');
        sessionStorage.removeItem('mobileok_txId');

        if (returnPath) {
          const returnUrl = new URL(returnPath);
          returnUrl.searchParams.set('authSuccess', 'true');
          returnUrl.searchParams.set('authMethod', 'mobileok');
          sessionStorage.setItem('mobileok_result', JSON.stringify(mobileOKResult));

          setStatusMessage('원래 페이지로 이동합니다...');
          setTimeout(() => {
            window.location.href = returnUrl.toString();
          }, 500);
        } else {
          sessionStorage.setItem('mobileok_result', JSON.stringify(mobileOKResult));
          setStatusMessage('원래 페이지로 이동합니다...');
          setTimeout(() => {
            window.history.back();
          }, 500);
        }
      }
    };

    const handleError = (err: Error) => {
      console.error('[MobileOKCallback] Auth error:', err);
      setError(err);
      setIsLoading(false);

      // 부모 창이 있으면 postMessage
      if (window.opener) {
        window.opener.postMessage({
          type: 'MOBILEOK_CALLBACK',
          success: 'false',
          message: err.message || '본인인증에 실패했습니다.',
        }, window.location.origin);
      } else {
        const returnPath = sessionStorage.getItem('auth_return_path');

        sessionStorage.removeItem('auth_return_path');
        sessionStorage.removeItem('mobileok_txId');

        if (returnPath) {
          const returnUrl = new URL(returnPath);
          returnUrl.searchParams.set('authSuccess', 'false');
          returnUrl.searchParams.set('authError', err.message);
          sessionStorage.setItem('mobileok_error', JSON.stringify({ message: err.message }));

          setTimeout(() => {
            window.location.href = returnUrl.toString();
          }, 2000);
        } else {
          sessionStorage.setItem('mobileok_error', JSON.stringify({ message: err.message }));
        }
      }
    };

    processCallback();
  }, [searchParams]);

  // 부모 창 존재 여부 로깅
  useEffect(() => {
    console.log('[MobileOKCallback] Component mounted');
    console.log('[MobileOKCallback] window.opener:', !!window.opener);
    console.log('[MobileOKCallback] auth_return_path:', sessionStorage.getItem('auth_return_path'));
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'sans-serif',
        backgroundColor: '#f5f5f5',
      }}
    >
      <div
        style={{
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center',
        }}
      >
        {isLoading && (
          <>
            <div
              style={{
                width: '40px',
                height: '40px',
                border: '4px solid #e0e0e0',
                borderTop: '4px solid #4a90d9',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px',
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {!isLoading && result && (
          <div
            style={{
              width: '50px',
              height: '50px',
              backgroundColor: '#4CAF50',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: 'white',
              fontSize: '24px',
            }}
          >
            ✓
          </div>
        )}

        {!isLoading && error && (
          <div
            style={{
              width: '50px',
              height: '50px',
              backgroundColor: '#f44336',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: 'white',
              fontSize: '24px',
            }}
          >
            ✕
          </div>
        )}

        <p style={{ fontSize: '16px', color: '#333', margin: 0 }}>
          {isLoading
            ? statusMessage
            : error
              ? error.message
              : '본인인증이 완료되었습니다.'}
        </p>

        {!isLoading && result && (
          <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
            {result.name}님 인증 완료
          </p>
        )}

        {!isLoading && (
          <button
            onClick={() => {
              const returnPath = sessionStorage.getItem('auth_return_path');
              if (returnPath) {
                window.location.href = returnPath;
              } else if (window.opener) {
                window.close();
              } else {
                window.history.back();
              }
            }}
            style={{
              marginTop: '20px',
              padding: '10px 24px',
              backgroundColor: '#4a90d9',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            {window.opener ? '창 닫기' : '돌아가기'}
          </button>
        )}
      </div>
    </div>
  );
}

export default MobileOKCallback;